import ContentWrapper from "@components/ContentWrapper/ContentWrapper";
import { Button, FCX, getRem, ID, Tooltip } from "@datassential/platform-ui-lib";
import { useCommonDataContext } from "@core/CommonDataContext";
import Loader from "@components/Loader";
import SelectFiltersProvider, { useSelectFilters } from "@vms/SelectFilters/SelectFiltersContext";
import { useMemo, useState } from "react";
import VisualizeHeader from "@pages/Visualize/VisualizeHeader";
import ContentWidget from "@components/ContentWidget/ContentWidget";
import VisualizeFilters from "@pages/Visualize/VisualizeFilters";
import { SelectAmongContextProvider, useSelectAmongContext } from "@vms/SelectAmong/SelectAmongContext";
import VisualizeTable from "@pages/Visualize/VisualizeTable";
import { useVisualizeData, VisualizeData } from "@api/useVisualizeData";
import VisualizeChart from "@pages/Visualize/VisualizeChart";
import ActionsBar from "@vms/ActionsBar";
import SelectedKeywords from "@vms/SelectedKeywords";
import { useVisualizeExport } from "@api/useVisualizeExport";
import { VisualizeContextProvider } from "@pages/Visualize/VisualizeContext";
import {
    SelectedKeywordsProvider, useFilterOutSelectedKeywords,
    useSelectedKeywordsContext
} from "@vms/SelectedKeywords/SelectedKeywordsContext";
import SelectedKeywordsViewsTabs from "@vms/SelectedKeywords/SelectedKeywordsViewsTabs";

export const CHART_ID = "visualize-scatter-chart";

const VisualizeWithContext = () => {
    const {
        selectedKeywordsIds,
        isSelectedKeywordsView,
    } = useSelectedKeywordsContext();
    const [activeItemId, setActiveItemId] = useState<ID>(undefined);
    const [reportData, setReportData] = useState<VisualizeData>([]);

    const { getMatchFilters } = useSelectFilters();

    const { amongPayload } = useSelectAmongContext();

    const { data: rawData, isLoading } = useVisualizeData(amongPayload);

    useFilterOutSelectedKeywords(rawData);

    const preSortedData = useMemo(
        () => [...rawData].sort(
            (a, b) => (a.name ?? '').localeCompare(b.name ?? '')
        ),
        [rawData]
    );

    const filteredData = useMemo(
        () => preSortedData.filter(getMatchFilters),
        [preSortedData, getMatchFilters]
    );

    const selectedKeywords = useMemo(
        () => preSortedData.filter(i => selectedKeywordsIds.includes(i.id)),
        [preSortedData, selectedKeywordsIds]
    );

    const viewData = useMemo(
        () => isSelectedKeywordsView ? selectedKeywords : filteredData,
        [isSelectedKeywordsView, selectedKeywords, filteredData]
    );

    const {
        isLoading: isReportLoading,
        get: getReport,
    } = useVisualizeExport(reportData);

    return (
        <ContentWrapper className="Visualize">
            <VisualizeHeader/>
            <ContentWidget
                key={isSelectedKeywordsView ? 'visualize' : 'all-items'}
                className="Visualize__content appears-top slow"
                testId="blockVisualize"
            >
                {(isLoading) && <Loader/>}
                <VisualizeFilters resultCount={filteredData.length}/>
                <div className="Visualize__chart-table-container">
                    <div className="Visualize__chart-container">
                        <VisualizeChart
                            data={viewData}
                            activeItemId={activeItemId}
                            setActiveItemId={setActiveItemId}
                        />
                    </div>

                    <VisualizeTable
                        data={viewData}
                        selectedKeywords={selectedKeywords}
                        isLoading={isLoading}
                        setActiveItemId={setActiveItemId}
                        setReportData={setReportData}
                    />
                </div>
            </ContentWidget>
            {(selectedKeywordsIds.length > 0) && (
                <ActionsBar>
                    <div style={{
                        display: 'flex',
                        width: '100%',
                        alignItems: 'center',
                        justifyContent: "center",
                        gap: getRem(32),
                    }}>
                        <SelectedKeywordsViewsTabs className="appears-top del-1"/>
                        <SelectedKeywords
                            className="appears-top del-2"
                            style={{
                                width: getRem(190),
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: "center",
                            }}
                        />
                        <Button
                            className="appears-top del-3"
                            isDisabled={selectedKeywords.length > 75 || isReportLoading}
                            onClick={getReport}
                            style={{ width: getRem(150) }}
                            data-tooltip-id={selectedKeywords.length > 75 ? "visualize-report-limit" : undefined}
                        >
                            {isReportLoading && <div className="load-ellipsis">loading</div>}
                            {!isReportLoading && 'Download'}
                        </Button>
                        <Tooltip id="visualize-report-limit">
                            Max 75 items
                        </Tooltip>
                    </div>
                </ActionsBar>
            )}
        </ContentWrapper>
    );
};

const Visualize: FCX = () => {
    const { isLoading } = useCommonDataContext();

    if (isLoading) {
        return <Loader/>;
    }

    return (
        <SelectedKeywordsProvider>
            <SelectFiltersProvider>
                <SelectAmongContextProvider>
                    <VisualizeContextProvider>
                        <VisualizeWithContext/>
                    </VisualizeContextProvider>
                </SelectAmongContextProvider>
            </SelectFiltersProvider>
        </SelectedKeywordsProvider>
    )
}

export default Visualize;